import { useState } from "react";
import { Link } from "react-router-dom";
import usePageTitle from "@/hooks/usePageTitle";
import { 
  ArrowRight, 
  Clock, 
  Users, 
  Target,
  GraduationCap,
  School,
  BookOpen,
  Building2,
  Info,
  FileText,
  ChevronDown,
  ChevronUp
} from "lucide-react";

const BASE_URL = `${process.env.REACT_APP_BACKEND_URL}/api/files`;
const PHOTOS = {
  classe: `${BASE_URL}/alma-vivu/photos/outils-classe.webp`,
  roue: `${BASE_URL}/alma-vivu/photos/outils-roue.jpg`,
  fresque: `${BASE_URL}/alma-vivu/photos/outils-fresque.webp`,
  dessin: `${BASE_URL}/alma-vivu/photos/outils-dessin.webp`,
};

const categories = [
  { id: "primaires", label: "Écoles Primaires", icon: School, color: "var(--maquis)" },
  { id: "colleges", label: "Collèges", icon: BookOpen, color: "var(--terracotta)" },
  { id: "lycees", label: "Lycées", icon: GraduationCap, color: "var(--maquis-dark)" },
  { id: "sup", label: "Enseignement Supérieur & Centres de Formation", icon: Building2, color: "var(--terracotta-dark)" },
];

const workshops = {
  primaires: [
    {
      title: "Atelier Super Loopi",
      cible: "CE2 à CM2",
      temps: "1h30",
      groupe: "Classe entière (20 à 30 élèves)",
      objectifs: "Comprendre le cycle de vie des objets à travers le jouet, découvrir les principes de l'économie circulaire, prendre conscience de la réduction des déchets et favoriser le don, la réparation et le réemploi.",
      deroule: [
        "Introduction immersive : Théâtralisation d'une mission pour captiver les enfants et sauver les jouets",
        "Jeu collaboratif : Exploration en petits groupes des 6 étapes du cycle de vie des objets",
        "Restitution collective : Assemblage d'une structure géante pour visualiser la boucle circulaire",
        "Passage à l'action : Organisation d'une collecte solidaire dans l'école ou valorisation des jouets inutilisés",
      ],
    },
    {
      title: "Fresque des déchets",
      cible: "CM1 - CM2",
      temps: "50 min",
      groupe: "Classe entière (20 à 30 élèves)",
      objectifs: "Comprendre le traitement des déchets en France (organisation, impacts, enjeux écologiques) et imaginer des solutions concrètes pour réduire les déchets et le gaspillage.",
      deroule: [
        "Panorama des déchets : qu'est-ce qu'un déchet ? Que trouve-t-on dans nos poubelles ? Où vont nos déchets ? Que deviennent-ils ? Quelles sont les conséquences sur notre santé et l'environnement ?",
        "Mission Réduction des déchets : Comment réduire nos déchets ? Découverte des 5R et que faire à la maison ou dans l'établissement ?",
      ],
    },
    {
      title: "Inventons nos vies bas carbone (Version Jeunes)",
      cible: "CE2 à CM2",
      temps: "2h00",
      groupe: "Groupe ou classe entière (15 à 30 élèves)",
      objectifs: "Appréhender les causes du réchauffement climatique, découvrir de manière intuitive les ordres de grandeur de l'empreinte carbone sans culpabiliser, et apprendre à concevoir une vie future bas carbone désirable.",
      deroule: [
        "Compréhension du phénomène : Explication ludique de l'effet de serre additionnel lié aux activités humaines",
        "Ordres de grandeur et manipulation : Classement d'objets du quotidien et composition de l'empreinte de personnages à l'aide de cartes à l'échelle",
        "Projection dans le futur : Défi créatif où chaque élève s'imagine adulte et choisit des activités peu émettrices",
        "Trace individuelle : Fabrication d'un mini-catalogue individuel récapitulant leurs choix de vie future",
      ],
    },
    {
      title: "Fresque de la mobilité Junior",
      cible: "CM1 et CM2",
      temps: "2h00",
      groupe: "Classe entière (jusqu'à 30 élèves)",
      objectifs: "Comprendre les impacts environnementaux et sanitaires de nos modes de déplacement, et découvrir les alternatives de mobilité douce.",
      deroule: [
        "Espace de réflexion : Atelier collaboratif de positionnement de cartes illustrées représentant les transports",
        "Analyse des impacts : Échange guidé sur la qualité de l'air, le climat et la santé",
        "Solutions locales : Identification d'actions éco-mobiles applicables au quotidien (marche, vélo, transports en commun)",
      ],
    },
  ],
  colleges: [
    {
      title: "Futur en main",
      cible: "4ème et 3ème",
      temps: "2h00",
      groupe: "Groupe ou classe entière (15 à 30 élèves)",
      objectifs: "Sensibiliser à l'orientation scolaire et professionnelle en explorant les métiers de demain.",
      deroule: [
        "Mise en situation : Jeu d'exploration des métiers et des transformations d'activités",
        "Travail en équipes : Analyse des compétences utiles pour les métiers de demain",
        "Restitution & Débat : Présentation des métiers et échanges sur les parcours d'orientation et les opportunités professionnelles d'avenir",
      ],
    },
    {
      title: "Future of IA",
      cible: "6ème à 3ème",
      temps: "2h00 à 2h30",
      groupe: "Groupe ou classe entière (15 à 30 élèves)",
      objectifs: "Prendre conscience de la face cachée des technologies, de l'intelligence artificielle et du numérique (extraction de métaux, consommation d'énergie, e-déchets) et encourager la sobriété numérique.",
      deroule: [
        "Reconstitution du cycle de vie : Cartographie collective des impacts du matériel et des données virtuelles",
        "Réflexion éthique et sociale : Analyse des usages du numérique et des effets sur le quotidien",
        "Co-construction de solutions : Définition d'éco-gestes numériques à adopter au collège et à la maison",
      ],
    },
    {
      title: "Fresques Jeunes thématiques",
      subtitle: "Économie circulaire, Déchets, Biodiversité, Climat et Mobilité",
      cible: "6ème à 3ème",
      temps: "2h00 à 2h30",
      groupe: "Groupe ou classe entière (15 à 30 élèves)",
      objectifs: "Développer la pensée systémique, comprendre les liens de cause à effet sur les enjeux environnementaux et renforcer la cohésion de groupe.",
      deroule: [
        "Réflexion collective : Positionnement concerté de cartes thématiques sur une grande fresque",
        "Créativité & Ancrage : Personnalisation graphique de la fresque (titre, liens, illustrations)",
        "Restitution & Engagement : Échange sur les ressentis et émergence de solutions concrètes à mettre en œuvre",
      ],
    },
  ],
  lycees: [
    {
      title: "Défi circulaire",
      cible: "Seconde à Terminale",
      temps: "2h00",
      groupe: "Classe entière",
      objectifs: "S'initier à l'éco-conception, apprendre à repenser un produit ou service en boucle fermée et stimuler l'innovation responsable.",
      deroule: [
        "Diagnostic linéaire : Analyse d'un produit du quotidien (extraction, fabrication, déchet)",
        "Atelier de reconception : Travail en sous-groupes pour appliquer des principes de circularité (réparation, réemploi, réutilisation)",
        "Pitch des projets : Présentation orale des solutions circulaires imaginées devant la classe",
      ],
    },
    {
      title: "Domino de la biodiversité",
      cible: "Seconde à Terminale",
      temps: "1h00",
      groupe: "Classe entière (15 à 30 élèves)",
      objectifs: "Comprendre l'érosion de la biodiversité, analyser les pressions humaines sur les écosystèmes et mesurer l'interconnexion du vivant.",
      deroule: [
        "Assemblage guidé : Positionnement des cartes de biodiversité et des facteurs de pression anthropique",
        "Analyse des cascades : Visualisation des réactions en chaîne et des ruptures d'équilibre écologique",
        "Solutions & Résilience : Élaboration d'actions de préservation et de restauration de la biodiversité à l'échelle locale",
      ],
    },
    {
      title: "La Bataille de l'IA & La Bataille de la Tech",
      cible: "Seconde à Terminale",
      temps: "2h00",
      groupe: "Groupe ou classe entière (15 à 30 élèves)",
      objectifs: "Analyser les impacts environnementaux, éthiques et sociaux du numérique et de l'intelligence artificielle générative, et élaborer des feuilles de route de sobriété technologique.",
      deroule: [
        "Jeu stratégique : Confrontation des usages technologiques aux limites physiques de la planète",
        "Analyse d'impacts : Cartographie des conséquences de la fabrication, du stockage et du traitement des données",
        "Feuille de route : Co-construction de règles d'usage raisonné et sobre de la tech au sein des organisations",
      ],
    },
    {
      title: "Fresque des frontières planétaires",
      cible: "Seconde à Terminale",
      temps: "3h00",
      groupe: "12 à 25 personnes",
      objectifs: "Prendre conscience des 9 limites planétaires qui définissent l'espace de sécurité de l'humanité et comprendre la nécessité de maintenir nos activités sous ces seuils critiques.",
      deroule: [
        "Cartographie systémique : Reconstitution progressive des 9 limites planétaires et de leurs interactions",
        "Analyse des dépassements : Évaluation de l'état actuel de la planète et des risques majeurs",
        "Changement de modèle : Réflexion collective sur les trajectoires de sobriété et de régénération à adopter",
      ],
    },
  ],
  sup: [
    {
      title: "Pois Chiche Party (Atelier RSE & Engagement)",
      cible: "Étudiants, apprenants en centres de formation, équipes éducatives, salariés et professionnels",
      temps: "2h30 à 3h00",
      groupe: "15 à 30 personnes",
      objectifs: "Aborder la Responsabilité Sociétale des Entreprises (RSE) de manière originale et participative, stimuler la cohésion d'équipe autour des enjeux de durabilité et déclencher une dynamique d'engagement interne.",
      deroule: [
        "Mise en dynamique RSE : Activités ludiques d'ouverture pour aborder les piliers de la responsabilité sociétale",
        "Atelier d'intelligence collective : Décodage collaboratif des enjeux environnementaux et sociaux appliqués aux organisations",
        "Co-construction de la démarche : Élaboration de propositions d'actions RSE concrètes à déployer dans sa structure",
      ],
    },
    {
      title: "La Bataille de l'IA & La Bataille de la Tech",
      cible: "Étudiants, apprenants en centres de formation, formateurs et professionnels",
      temps: "2h00",
      groupe: "12 à 25 personnes",
      objectifs: "Analyser les impacts environnementaux, éthiques et sociaux du numérique et de l'intelligence artificielle, et élaborer des feuilles de route de sobriété technologique.",
      deroule: [
        "Jeu stratégique : Confrontation des usages technologiques aux limites physiques de la planète",
        "Analyse d'impacts : Cartographie des conséquences de la fabrication, du stockage et du traitement des données",
        "Feuille de route : Co-construction de règles d'usage raisonné et sobre de la tech au sein des organisations",
      ],
    },
    {
      title: "Inventons nos vies bas carbone",
      cible: "Équipes éducatives, formateurs, enseignants, salariés, professionnels et citoyens",
      temps: "2h30 à 3h00",
      groupe: "10 à 25 personnes",
      objectifs: "Assimiler les ordres de grandeur de la Stratégie Nationale Bas Carbone (SNBC) et du budget cible de 2 tonnes de CO2e/an, et construire une feuille de route d'action individuelle et collective.",
      deroule: [
        "Constat et ordres de grandeur : Manipulation de cartes d'émissions à l'échelle (alimentation, transport, logement, consommation, services publics)",
        "Mise en situation : Évaluation de son empreinte actuelle et comparaison avec les objectifs climatiques",
        "Co-construction de scénarios : Exploration de solutions enthousiasmantes pour réduire ses émissions sans perte de qualité de vie",
        "Plan d'action : Formulation de « Prochains Petits Pas » précis et réalisables immédiatement",
      ],
    },
  ],
};

const WorkshopCard = ({ workshop, categoryColor }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="card-premium overflow-hidden" data-testid={`workshop-${workshop.title.toLowerCase().replace(/\s+/g, '-').substring(0, 30)}`}>
      <div className="p-6 md:p-8">
        {/* Title */}
        <h3 className="font-display text-xl font-bold text-[var(--charcoal)] mb-1">
          {workshop.title}
        </h3>
        {workshop.subtitle && (
          <p className="text-sm text-[var(--stone)] italic mb-4">{workshop.subtitle}</p>
        )}

        {/* Meta */}
        <div className="flex flex-wrap gap-3 my-4">
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--sand)] rounded-full text-xs font-medium text-[var(--charcoal)]">
            <Target className="w-3.5 h-3.5" /> {workshop.cible}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--sand)] rounded-full text-xs font-medium text-[var(--charcoal)]">
            <Clock className="w-3.5 h-3.5" /> {workshop.temps}
          </span>
          <span className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[var(--sand)] rounded-full text-xs font-medium text-[var(--charcoal)]">
            <Users className="w-3.5 h-3.5" /> {workshop.groupe}
          </span>
        </div>

        {/* Objectifs */}
        <div className="mb-4">
          <p className="text-xs uppercase tracking-[0.15em] font-bold mb-2" style={{ color: categoryColor }}>Objectifs pédagogiques</p>
          <p className="text-[var(--stone)] text-sm leading-relaxed">{workshop.objectifs}</p>
        </div>

        {/* Déroulé toggle */}
        <button
          onClick={() => setExpanded(!expanded)}
          className="flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-80"
          style={{ color: categoryColor }}
        >
          {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          {expanded ? "Masquer le déroulé" : "Voir le déroulé"}
        </button>

        {expanded && (
          <div className="mt-4 space-y-2 animate-fade-in">
            <p className="text-xs uppercase tracking-[0.15em] font-bold mb-3" style={{ color: categoryColor }}>Déroulé</p>
            {workshop.deroule.map((step, idx) => (
              <div key={idx} className="flex items-start gap-3 p-3 bg-[var(--sand)]/50 rounded-xl">
                <span className="w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 text-white" style={{ backgroundColor: categoryColor }}>
                  {idx + 1}
                </span>
                <p className="text-sm text-[var(--stone)] leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 md:px-8 pb-6 md:pb-8">
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-medium text-white transition-colors hover:opacity-90"
          style={{ backgroundColor: categoryColor }}
          data-testid={`devis-btn-${workshop.title.toLowerCase().replace(/\s+/g, '-').substring(0, 20)}`}
        >
          <FileText className="w-4 h-4" />
          Demander un devis
        </Link>
      </div>
    </div>
  );
};

const Podcast = () => {
  const [activeCategory, setActiveCategory] = useState("primaires");
  usePageTitle("Outils");
  const activeCat = categories.find(c => c.id === activeCategory);

  return (
    <div className="min-h-screen bg-[var(--bone)]" data-testid="outils-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="blob-bg w-[500px] h-[500px] bg-[var(--terracotta)]/15 top-0 right-0"></div>
        <div className="blob-bg w-[300px] h-[300px] bg-[var(--maquis)]/10 bottom-0 left-0"></div>

        <div className="container-premium relative z-10">
          <div className="max-w-3xl">
            <h1 className="font-display text-5xl md:text-6xl font-bold text-[var(--charcoal)] mb-6 leading-tight">
              <span className="text-[var(--terracotta)] emphasis">Outils</span>
            </h1>
            <p className="text-xl text-[var(--stone)] mb-10 leading-relaxed">
              Des ateliers adaptés à chaque âge pour comprendre et agir sur des sujets concrets.
            </p>
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="pb-16" data-testid="stats-section">
        <div className="container-premium">
          <div className="grid grid-cols-3 gap-6 max-w-3xl mx-auto">
            <div className="text-center card-premium p-6">
              <div className="font-display text-4xl font-bold text-[var(--terracotta)]">60</div>
              <div className="text-sm text-[var(--stone)] mt-1">ateliers</div>
            </div>
            <div className="text-center card-premium p-6">
              <div className="font-display text-4xl font-bold text-[var(--maquis)]">890</div>
              <div className="text-sm text-[var(--stone)] mt-1">personnes sensibilisées</div>
            </div>
            <div className="text-center card-premium p-6">
              <div className="font-display text-4xl font-bold text-[var(--terracotta-dark)]">11</div>
              <div className="text-sm text-[var(--stone)] mt-1">formations certifiées</div>
            </div>
          </div>
        </div>
      </section>

      {/* Photos Gallery */}
      <section className="pb-16" data-testid="photos-section">
        <div className="container-premium">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
              <img src={PHOTOS.classe} alt="Atelier en classe" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
              <img src={PHOTOS.roue} alt="Roue de l'économie circulaire" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
              <img src={PHOTOS.fresque} alt="Fresque collaborative" className="w-full h-full object-cover" />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg aspect-[4/3]">
              <img src={PHOTOS.dessin} alt="Enfant dessinant sur la fresque" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="pb-20" data-testid="workshops-section">
        <div className="container-premium">
          <div className="max-w-4xl mx-auto">
            {/* Tabs */}
            <div className="flex flex-wrap gap-2 mb-10">
              {categories.map((cat) => {
                const Icon = cat.icon;
                return (
                  <button
                    key={cat.id}
                    onClick={() => setActiveCategory(cat.id)}
                    data-testid={`tab-${cat.id}`}
                    className={`flex items-center gap-2 px-5 py-3 rounded-full transition-all font-medium text-sm ${
                      activeCategory === cat.id
                        ? 'text-white shadow-lg'
                        : 'bg-[var(--sand)] text-[var(--charcoal)] hover:bg-[var(--sand-dark)]'
                    }`}
                    style={activeCategory === cat.id ? { backgroundColor: cat.color } : {}}
                  >
                    <Icon className="w-4 h-4" />
                    {cat.label}
                  </button>
                );
              })}
            </div>

            {/* Funding notice for collèges and lycées */}
            {(activeCategory === "colleges" || activeCategory === "lycees") && (
              <div className="card-premium p-5 mb-8 border-l-4 border-[var(--terracotta)] bg-[var(--sand)]/50 animate-fade-in" data-testid="funding-notice">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-[var(--terracotta)] mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-[var(--stone)] leading-relaxed">
                    <strong className="text-[var(--charcoal)]">Financement disponible :</strong> Tous les ateliers proposés pour les {activeCategory === "colleges" ? "collèges" : "lycées"} sont référencés et finançables via le dispositif <strong className="text-[var(--charcoal)]">Pass Culture</strong> sur la plateforme <strong className="text-[var(--charcoal)]">ADAGE</strong>.
                  </p>
                </div>
              </div>
            )}

            {/* Workshop Cards */}
            <div className="space-y-6 animate-fade-in">
              {workshops[activeCategory].map((workshop, idx) => (
                <WorkshopCard 
                  key={`${activeCategory}-${idx}`} 
                  workshop={workshop} 
                  categoryColor={activeCat.color} 
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-premium relative overflow-hidden">
        <div className="blob-bg w-[500px] h-[500px] bg-[var(--terracotta)]/20 top-0 right-0"></div>
        <div className="container-premium relative z-10 text-center">
          <h2 className="font-display text-4xl md:text-5xl font-bold text-[var(--charcoal)] mb-8">
            Prêt à transmettre<br />
            <span className="text-[var(--terracotta)] emphasis">ensemble</span> ?
          </h2>
          <p className="text-[var(--stone)] text-xl max-w-2xl mx-auto mb-12">
            Faisons le point ensemble sur vos enjeux et construisons un parcours adapté à votre contexte et vos attentes.
          </p>
          <Link to="/contact">
            <button className="btn-primary text-lg px-10 py-5">
              Prendre rendez-vous
            </button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Podcast;
